/**
 * AdminSecurityPage - Login / session security operator dashboard.
 *
 * Mirrors the doc-spec §4.1 layout adapted to Buyist Support's design
 * system. Four 24h stat cards, a most-targeted-emails strip, then two
 * tabs (Login Attempts default + Active Sessions).
 *
 * See plans/2026-05-18-bot-detection-and-audit.md Phase 7.
 */
import { useMemo, useState } from "react";

import { Alert, Badge, Button, Card, Input, Select, Spinner } from "@/components/ui";
import { useAdminActiveSessions } from "@/hooks/useAdminActiveSessions";
import { useAdminLoginAttempts } from "@/hooks/useAdminLoginAttempts";
import { useAdminSecurityStats } from "@/hooks/useAdminSecurityStats";
import { usePageTitle } from "@/hooks/usePageTitle";
import type { LoginAttemptsQuery } from "@/types/api";

const PAGE_SIZE = 50;

const SUCCESS_OPTIONS = [
  { value: "", label: "Any result" },
  { value: "true", label: "Success only" },
  { value: "false", label: "Failed only" },
];

type Tab = "attempts" | "sessions";

interface AttemptsFilters {
  success: "" | "true" | "false";
  email: string;
  ip_address: string;
}

const EMPTY_ATTEMPTS: AttemptsFilters = {
  success: "",
  email: "",
  ip_address: "",
};

function attemptsQuery(
  f: AttemptsFilters,
  offset: number,
): LoginAttemptsQuery {
  const q: LoginAttemptsQuery = { limit: PAGE_SIZE, offset };
  if (f.success === "true") q.success = true;
  if (f.success === "false") q.success = false;
  if (f.email.trim()) q.email = f.email.trim();
  if (f.ip_address.trim()) q.ip_address = f.ip_address.trim();
  return q;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function AdminSecurityPage(): JSX.Element {
  usePageTitle("Security");

  const stats = useAdminSecurityStats();

  const [tab, setTab] = useState<Tab>("attempts");

  // Login-attempts state.
  const [attemptFilters, setAttemptFilters] =
    useState<AttemptsFilters>(EMPTY_ATTEMPTS);
  const [attemptOffset, setAttemptOffset] = useState(0);
  const attemptQ = useMemo(
    () => attemptsQuery(attemptFilters, attemptOffset),
    [attemptFilters, attemptOffset],
  );
  const attempts = useAdminLoginAttempts({
    query: attemptQ,
    enabled: tab === "attempts",
  });

  // Active-sessions state.
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionOffset, setSessionOffset] = useState(0);
  const sessions = useAdminActiveSessions({
    query: {
      search: sessionSearch.trim() || undefined,
      limit: PAGE_SIZE,
      offset: sessionOffset,
    },
    enabled: tab === "sessions",
  });

  return (
    <div className="mx-auto w-full max-w-7xl">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-neutral-900">
          <i
            className="fa-duotone fa-thin fa-shield-keyhole mr-2 text-primary-500"
            aria-hidden="true"
          />
          Security
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Failed logins, active sessions, and most-targeted accounts.
          Data refreshes once a minute.
        </p>
      </header>

      {/* Stat cards. */}
      {stats.error && (
        <Alert variant="error" title="Failed to load stats" className="mb-4">
          {stats.error instanceof Error
            ? stats.error.message
            : "Unknown error."}
        </Alert>
      )}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Failed logins (24h)"
          value={stats.data?.failed_24h}
          tone="red"
          icon="fa-circle-xmark"
        />
        <StatCard
          label="Successful logins (24h)"
          value={stats.data?.success_24h}
          tone="green"
          icon="fa-circle-check"
        />
        <StatCard
          label="Unique failed IPs (24h)"
          value={stats.data?.unique_failed_ips_24h}
          tone="amber"
          icon="fa-network-wired"
        />
        <StatCard
          label="Active sessions"
          value={stats.data?.active_sessions}
          tone="blue"
          icon="fa-user-shield"
        />
      </div>

      {/* Most-targeted emails strip. */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-neutral-700">
            Most targeted emails (24h):
          </span>
          {stats.isLoading && <Spinner size="sm" />}
          {!stats.isLoading &&
            (stats.data?.top_targeted_emails.length ?? 0) === 0 && (
              <span className="text-sm text-neutral-500">
                No failed-login activity in the last 24 hours.
              </span>
            )}
          {stats.data?.top_targeted_emails.map((row) => (
            <Badge key={row.email} variant="warning">
              {row.email} ({row.count})
            </Badge>
          ))}
        </div>
      </Card>

      {/* Tabs. */}
      <div
        className="mb-3 flex gap-1 border-b border-neutral-200"
        role="tablist"
        aria-label="Security view"
      >
        <TabButton
          active={tab === "attempts"}
          onClick={() => {
            setTab("attempts");
            setAttemptOffset(0);
          }}
        >
          Login Attempts
        </TabButton>
        <TabButton
          active={tab === "sessions"}
          onClick={() => {
            setTab("sessions");
            setSessionOffset(0);
          }}
        >
          Active Sessions
        </TabButton>
      </div>

      {tab === "attempts" && (
        <AttemptsTab
          filters={attemptFilters}
          onFilters={(next) => {
            setAttemptFilters(next);
            setAttemptOffset(0);
          }}
          offset={attemptOffset}
          onOffset={setAttemptOffset}
          isLoading={attempts.isLoading}
          error={attempts.error}
          data={attempts.data}
        />
      )}

      {tab === "sessions" && (
        <SessionsTab
          search={sessionSearch}
          onSearch={(s) => {
            setSessionSearch(s);
            setSessionOffset(0);
          }}
          offset={sessionOffset}
          onOffset={setSessionOffset}
          isLoading={sessions.isLoading}
          error={sessions.error}
          data={sessions.data}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: number | undefined;
  tone: "red" | "green" | "amber" | "blue";
  icon: string;
}

const TONE_CLASSES: Record<StatCardProps["tone"], string> = {
  red: "text-red-600",
  green: "text-emerald-600",
  amber: "text-amber-600",
  blue: "text-blue-600",
};

function StatCard({ label, value, tone, icon }: StatCardProps): JSX.Element {
  return (
    <Card padding="md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {label}
          </p>
          <p className={`mt-1 text-3xl font-semibold ${TONE_CLASSES[tone]}`}>
            {value ?? "-"}
          </p>
        </div>
        <i
          className={`fa-duotone fa-thin ${icon} text-3xl ${TONE_CLASSES[tone]}`}
          aria-hidden="true"
        />
      </div>
    </Card>
  );
}

function TabButton(props: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const { active, onClick, children } = props;
  const base =
    "rounded-t-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500";
  const cls = active
    ? `${base} border border-b-0 border-neutral-200 bg-white text-neutral-900`
    : `${base} text-neutral-500 hover:text-neutral-700`;
  return (
    <button type="button" role="tab" aria-selected={active} className={cls} onClick={onClick}>
      {children}
    </button>
  );
}

interface AttemptsTabProps {
  filters: AttemptsFilters;
  onFilters: (next: AttemptsFilters) => void;
  offset: number;
  onOffset: (next: number) => void;
  isLoading: boolean;
  error: unknown;
  data: { attempts: Array<{ id: number; email: string; ip_address: string | null; success: boolean; failure_reason: string | null; created_at: string }>; total: number } | undefined;
}

function AttemptsTab(props: AttemptsTabProps): JSX.Element {
  const { filters, onFilters, offset, onOffset, isLoading, error, data } = props;
  const rows = data?.attempts ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card padding="sm" className="mb-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Select
            id="sec-attempt-success"
            label="Result"
            options={SUCCESS_OPTIONS}
            value={filters.success}
            onChange={(e) =>
              onFilters({
                ...filters,
                success: e.target.value as "" | "true" | "false",
              })
            }
          />
          <Input
            id="sec-attempt-email"
            label="Email"
            value={filters.email}
            placeholder="alice@example.com"
            onChange={(e) =>
              onFilters({ ...filters, email: e.target.value })
            }
          />
          <Input
            id="sec-attempt-ip"
            label="IP address"
            value={filters.ip_address}
            placeholder="10.0.0.1"
            onChange={(e) =>
              onFilters({ ...filters, ip_address: e.target.value })
            }
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onFilters(EMPTY_ATTEMPTS)}
          >
            Clear filters
          </Button>
        </div>
      </Card>

      {error ? (
        <Alert variant="error" title="Failed to load login attempts">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      ) : null}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <Th>Time</Th>
                <Th>Email</Th>
                <Th>IP</Th>
                <Th>Result</Th>
                <Th>Failure reason</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No login attempts match this filter.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="text-sm">
                  <td className="px-3 py-2 text-neutral-700">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="px-3 py-2 font-medium text-neutral-900">
                    {row.email}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {row.ip_address ?? "-"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={row.success ? "success" : "error"}>
                      {row.success ? "Success" : "Failed"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {row.failure_reason ?? "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Pagination
        offset={offset}
        onOffset={onOffset}
        total={total}
        unit="attempts"
      />
    </>
  );
}

interface SessionsTabProps {
  search: string;
  onSearch: (s: string) => void;
  offset: number;
  onOffset: (next: number) => void;
  isLoading: boolean;
  error: unknown;
  data: { sessions: Array<{ id: string; user_id: string; user_email: string; user_name: string | null; device_name: string | null; ip_address: string | null; last_active_at: string; created_at: string }>; total: number } | undefined;
}

function SessionsTab(props: SessionsTabProps): JSX.Element {
  const { search, onSearch, offset, onOffset, isLoading, error, data } = props;
  const rows = data?.sessions ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card padding="sm" className="mb-3">
        <Input
          id="sec-sessions-search"
          label="Search by user email or name"
          value={search}
          placeholder="alice@example.com or Alice"
          onChange={(e) => onSearch(e.target.value)}
        />
      </Card>

      {error ? (
        <Alert variant="error" title="Failed to load sessions">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      ) : null}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <Th>User</Th>
                <Th>Device</Th>
                <Th>IP</Th>
                <Th>Last active</Th>
                <Th>Started</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No active sessions.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="text-sm">
                  <td className="px-3 py-2">
                    <div className="font-medium text-neutral-900">
                      {row.user_name ?? row.user_email}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {row.user_email}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {row.device_name ?? "-"}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {row.ip_address ?? "-"}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {formatDate(row.last_active_at)}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {formatDate(row.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Pagination
        offset={offset}
        onOffset={onOffset}
        total={total}
        unit="sessions"
      />
    </>
  );
}

function Th(props: { children: React.ReactNode }): JSX.Element {
  return (
    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
      {props.children}
    </th>
  );
}

function Pagination(props: {
  offset: number;
  onOffset: (next: number) => void;
  total: number;
  unit: string;
}): JSX.Element {
  const { offset, onOffset, total, unit } = props;
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + PAGE_SIZE, total);
  return (
    <div className="mt-3 flex items-center justify-between">
      <span className="text-sm text-neutral-600">
        {total === 0
          ? `0 ${unit}`
          : `${start} - ${end} of ${total} ${unit}`}
      </span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => onOffset(offset + PAGE_SIZE)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default AdminSecurityPage;
